// Admin CRUD for the driver dashboard's static Notifications message board
// -- same shape as VehicleCheckItemsSettings.tsx (load all, inline edit,
// add-new form, delete). Only the lowest-sort_order 3 active messages show
// on the driver side (see src/lib/driverNoticeBoard.ts) -- everything here
// is the full list, active or not, so turning a message back on later
// doesn't mean re-typing it.
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { Plus, Save, X, Edit, Trash2, Megaphone } from "lucide-react";
import { useAppContext } from "@/contexts/AppContext";
import { NOTICE_BOARD_DISPLAY_LIMIT, noticeBoardTitle } from "@/lib/driverNoticeBoard";

const DEFAULT_TENANT_ID = "1225f4cb-43ae-463d-a6b0-53f721362dbf";

interface NoticeRow {
  id: string;
  title: string | null;
  message: string;
  is_active: boolean;
  sort_order: number;
  created_by: string | null;
  created_at: string;
}

const emptyNewForm = { title: "", message: "", sort_order: 100, is_active: true };

const DriverNoticeBoardSettings: React.FC = () => {
  const { currentUser } = useAppContext();
  const [notices, setNotices] = useState<NoticeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<NoticeRow>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newForm, setNewForm] = useState(emptyNewForm);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("driver_notice_board_messages")
      .select("*")
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false });

    if (error) {
      toast({ title: "Error", description: "Failed to load notices: " + error.message, variant: "destructive" });
    } else {
      setNotices(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const activeCount = notices.filter((n) => n.is_active).length;

  const startEdit = (n: NoticeRow) => {
    setEditingId(n.id);
    setEditForm({ ...n });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    if (!editingId || !editForm.message?.trim()) {
      toast({ title: "Error", description: "Message text is required", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("driver_notice_board_messages")
      .update({
        title: editForm.title?.trim() || null,
        message: editForm.message.trim(),
        sort_order: editForm.sort_order ?? 100,
        is_active: editForm.is_active ?? true,
      })
      .eq("id", editingId);

    if (error) {
      toast({ title: "Error", description: "Failed to update: " + error.message, variant: "destructive" });
    } else {
      toast({ title: "Saved" });
      setEditingId(null);
      setEditForm({});
      load();
    }
    setSaving(false);
  };

  const addNotice = async () => {
    const message = newForm.message.trim();
    if (!message) {
      toast({ title: "Error", description: "Message text is required", variant: "destructive" });
      return;
    }

    setSaving(true);
    const { error } = await supabase.from("driver_notice_board_messages").insert({
      tenant_id: DEFAULT_TENANT_ID,
      title: newForm.title.trim() || null,
      message,
      sort_order: newForm.sort_order,
      is_active: newForm.is_active,
      created_by: currentUser?.driver_name || null,
    });

    if (error) {
      toast({ title: "Error", description: "Failed to add: " + error.message, variant: "destructive" });
    } else {
      toast({ title: "Added", description: "Notice added to the board" });
      setShowAddForm(false);
      setNewForm(emptyNewForm);
      load();
    }
    setSaving(false);
  };

  const deleteNotice = async (n: NoticeRow) => {
    if (!confirm(`Delete this notice? This can't be undone.\n\n"${n.message}"`)) return;
    const { error } = await supabase.from("driver_notice_board_messages").delete().eq("id", n.id);
    if (error) {
      toast({ title: "Error", description: "Failed to delete: " + error.message, variant: "destructive" });
    } else {
      toast({ title: "Deleted" });
      load();
    }
  };

  return (
    <div className="rounded-xl border border-yellow-900/20 bg-black/20 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-foreground flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-yellow-500" />
            Driver Notices
          </h4>
          <p className="text-xs text-muted-foreground mt-1">
            Static messages pinned to the top of every driver's Notifications tab -- for things like roster
            deadlines or a heads-up that doesn't need the full Comms Book treatment. Only the {NOTICE_BOARD_DISPLAY_LIMIT} active
            notices with the lowest Sort Order show at once (ties broken by newest), so add more than {NOTICE_BOARD_DISPLAY_LIMIT} if you
            like -- just set Sort Order to control which ones surface. Turn a notice off instead of deleting it if
            you might want it back.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowAddForm((s) => !s)}
          className="border-yellow-600 text-yellow-400 hover:bg-yellow-900"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Notice
        </Button>
      </div>

      {activeCount > NOTICE_BOARD_DISPLAY_LIMIT && (
        <p className="text-xs text-amber-400">
          {activeCount} notices are active -- only the top {NOTICE_BOARD_DISPLAY_LIMIT} (by Sort Order) show on a driver's dashboard.
        </p>
      )}

      {showAddForm && (
        <div className="space-y-3 p-3 rounded-lg border border-gray-600 bg-gray-800/50">
          <div>
            <Label className="text-yellow-500 text-xs">Heading (shown on the tile)</Label>
            <Input
              value={newForm.title}
              onChange={(e) => setNewForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Monday Closing Reminder"
            />
          </div>
          <div>
            <Label className="text-yellow-500 text-xs">Message (shown once a driver opens it)</Label>
            <Textarea
              value={newForm.message}
              onChange={(e) => setNewForm((f) => ({ ...f, message: e.target.value }))}
              placeholder="e.g. Please make sure all jobs and days are closed off by 12:00 noon every Monday."
              rows={3}
            />
          </div>
          <div className="flex items-end gap-3">
            <div className="w-32">
              <Label className="text-yellow-500 text-xs">Sort Order</Label>
              <Input
                type="number"
                value={newForm.sort_order}
                onChange={(e) => setNewForm((f) => ({ ...f, sort_order: parseInt(e.target.value, 10) || 100 }))}
              />
            </div>
            <div className="flex items-center gap-2 pb-2">
              <Checkbox
                checked={newForm.is_active}
                onCheckedChange={(v) => setNewForm((f) => ({ ...f, is_active: !!v }))}
              />
              <Label className="text-sm">Active</Label>
            </div>
            <div className="flex-1" />
            <Button size="sm" variant="ghost" onClick={() => { setShowAddForm(false); setNewForm(emptyNewForm); }}>
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
            <Button size="sm" onClick={addNotice} disabled={saving} className="golden-button">
              <Save className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : notices.length === 0 ? (
        <p className="text-sm text-muted-foreground">No notices yet.</p>
      ) : (
        <div className="space-y-2">
          {notices.map((n) =>
            editingId === n.id ? (
              <div key={n.id} className="space-y-3 p-3 rounded-lg border border-gray-600 bg-gray-800/50">
                <div>
                  <Label className="text-yellow-500 text-xs">Heading (shown on the tile)</Label>
                  <Input
                    value={editForm.title || ""}
                    onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="e.g. Monday Closing Reminder"
                  />
                </div>
                <div>
                  <Label className="text-yellow-500 text-xs">Message (shown once a driver opens it)</Label>
                  <Textarea
                    value={editForm.message || ""}
                    onChange={(e) => setEditForm((f) => ({ ...f, message: e.target.value }))}
                    rows={3}
                  />
                </div>
                <div className="flex items-end gap-3">
                  <div className="w-32">
                    <Label className="text-yellow-500 text-xs">Sort Order</Label>
                    <Input
                      type="number"
                      value={editForm.sort_order ?? 100}
                      onChange={(e) => setEditForm((f) => ({ ...f, sort_order: parseInt(e.target.value, 10) || 100 }))}
                    />
                  </div>
                  <div className="flex items-center gap-2 pb-2">
                    <Checkbox
                      checked={editForm.is_active ?? true}
                      onCheckedChange={(v) => setEditForm((f) => ({ ...f, is_active: !!v }))}
                    />
                    <Label className="text-sm">Active</Label>
                  </div>
                  <div className="flex-1" />
                  <Button size="sm" variant="ghost" onClick={cancelEdit}>
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                  <Button size="sm" onClick={saveEdit} disabled={saving} className="golden-button">
                    <Save className="w-4 h-4 mr-1" />
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <div
                key={n.id}
                className={`p-3 rounded-lg border border-gray-700 flex items-start justify-between gap-3 ${
                  n.is_active ? "bg-gray-800/30" : "bg-gray-900/40 opacity-60"
                }`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] text-muted-foreground">Sort {n.sort_order}</span>
                    {!n.is_active && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-700 text-gray-300">Inactive</span>
                    )}
                    {n.created_by && <span className="text-[10px] text-muted-foreground">-- {n.created_by}</span>}
                  </div>
                  <p className="text-sm font-medium text-foreground mt-0.5">{noticeBoardTitle(n)}</p>
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap mt-0.5">{n.message}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(n)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteNotice(n)} className="text-red-400 hover:text-red-300">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default DriverNoticeBoardSettings;
